import { Box, styled } from '@mui/material'
import Grid from '@mui/material/Unstable_Grid2'
import { Pow18 } from 'config'
import dayjs from 'dayjs'
import { useAtom, useAtomValue } from 'jotai'
import numbro from 'numbro'
import { memo, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { bn, e2w, w2e } from 'utils/number'
import { contractInformationAtom, contractInformationSymbolAtom } from '../atom'
import { COMMUNITY_LINKS } from '../../../../constants/community'

export const GridBox = styled(Box)`
  width: 100%;
  font-size: 13px;
  line-height: 16px;
`

export const GridBody = styled(Grid)`
  height: 48px;
  border-bottom: 1px solid #2e363b;
`
export const GridHead = styled(Grid)`
  color: ${(props) => props.theme.color.text70};
  font-weight: 400;
  font-size: 12px;
  border-bottom: 1px solid ${(props) => props.theme.color.bgGray};
`
GridHead.defaultProps = GridBody.defaultProps = {
  container: true,
}

export const GridItem = styled(Grid)`
  padding: 12px 16px;
  display: flex;
  align-items: center;
  font-size: 14px;

  &.title {
    color: rgba(149, 164, 173, 1);
    font-size: 12px;
  }

  a {
    color: ${(props) => props.theme.color.primary30};
  }
`

export const ContractInfoPanel = memo(() => {
  const [information] = useAtom(contractInformationAtom)
  const currentSymbol = useAtomValue(contractInformationSymbolAtom)

  const contractSize = useMemo(() => {
    if (!information?.markPrice) {
      return '0'
    }
    return w2e(bn(e2w(1)).mul(Pow18).div(e2w(information.markPrice)))
  }, [information])

  return (
    <GridBox>
      {/* <GridHead>
        <GridItem>Full Contract Details</GridItem>
      </GridHead> */}
      <GridBody>
        <GridItem xs={4} className="title">
          Ticker Root
        </GridItem>
        <GridItem xs={8}>{currentSymbol}</GridItem>
      </GridBody>
      <GridBody>
        <GridItem xs={4} className="title">
          Initial Margin
        </GridItem>
        <GridItem xs={8}>
          {numbro(information?.riskLimit?.initialMarginRate).format({
            output: 'percent',
            mantissa: 2,
          })}{' '}
          + Entry Taker Fee + Exit Taker Fee
        </GridItem>
      </GridBody>
      <GridBody>
        <GridItem xs={4} className="title">
          Maint Margin
        </GridItem>
        <GridItem xs={8}>
          {numbro(information?.riskLimit?.maintenanceMarginRate).format({
            output: 'percent',
            mantissa: 2,
          })}{' '}
          + Exit Taker Fee + Funding Rate
        </GridItem>
      </GridBody>
      <GridBody>
        <GridItem xs={4} className="title">
          Funding Rate
        </GridItem>
        <GridItem xs={8}>
          {numbro(information?.currentFundingRate).format({
            output: 'percent',
            mantissa: 4,
          })}
        </GridItem>
      </GridBody>
      <GridBody>
        <GridItem xs={4} className="title">
          Funding Interval
        </GridItem>
        <GridItem xs={8}>every {information?.fundingInterval} hours</GridItem>
      </GridBody>
      <GridBody>
        <GridItem xs={4} className="title">
          Next Funding
        </GridItem>
        <GridItem xs={8}>
          {dayjs(information?.nextFundingTime).toString()}
        </GridItem>
      </GridBody>
      <GridBody>
        <GridItem xs={4} className="title">
          Predicted Rate
        </GridItem>
        <GridItem xs={8}>
          {numbro(information?.nextFundingRate).format({
            output: 'percent',
            mantissa: 4,
          })}
        </GridItem>
      </GridBody>
      <GridBody>
        <GridItem xs={4} className="title">
          Mark Price
        </GridItem>
        <GridItem xs={8}>{information?.markPrice}</GridItem>
      </GridBody>
      <GridBody>
        <GridItem xs={4} className="title">
          Auto-Deleveraging Enabled
        </GridItem>
        <GridItem xs={8}>
          {information?.adlEnabled ? 'Yes' : 'No'}: This contract is{' '}
          <Link to="">highly speculative</Link> and manages loss through
          auto-deleveraging.
        </GridItem>
      </GridBody>
      <GridBody>
        <GridItem xs={4} className="title">
          Mark Method
        </GridItem>
        <GridItem xs={8}>{information?.markMethod}</GridItem>
      </GridBody>
      <GridBody>
        <GridItem xs={4} className="title">
          Open Interest
        </GridItem>
        <GridItem xs={8}>{information?.openInterest}</GridItem>
      </GridBody>
      <GridBody>
        <GridItem xs={4} className="title">
          24H Turnover
        </GridItem>
        <GridItem xs={8}>{information?.amount24H}</GridItem>
      </GridBody>
      <GridBody>
        <GridItem xs={4} className="title">
          Total Volume
        </GridItem>
        <GridItem xs={8}>{information?.volume24H}</GridItem>
      </GridBody>
      <GridBody>
        <GridItem xs={4} className="title">
          Type
        </GridItem>
        <GridItem xs={8}>{information?.type}</GridItem>
      </GridBody>
      <GridBody>
        <GridItem xs={4} className="title">
          Contract Size
        </GridItem>
        <GridItem xs={8}>
          1 USD (Currently {contractSize} {information?.baseCurrency} per
          contract)
        </GridItem>
      </GridBody>
      <GridBody>
        <GridItem xs={4} className="title">
          Commission
        </GridItem>
        <GridItem xs={8}>
          See the &nbsp;
          <Link to={COMMUNITY_LINKS.DOCS_TRADING_FEES} target="_blank">
            Fees Reference
          </Link>
          &nbsp; for more details.
        </GridItem>
      </GridBody>
      <GridBody>
        <GridItem xs={4} className="title">
          Taker Fee Rate
        </GridItem>
        <GridItem xs={8}>-</GridItem>
      </GridBody>
      <GridBody>
        <GridItem xs={4} className="title">
          Maker Fee Rate
        </GridItem>
        <GridItem xs={8}>-</GridItem>
      </GridBody>
    </GridBox>
  )
})
